// src/components/audit/AuditDetailModal.tsx
// Modal for viewing full audit record details

import React from 'react';
import { X, Clock, User, Package, FileText, ArrowRight, Database, Code } from 'lucide-react';
import { AuditRecord, getEventTypeDisplay, getActionColor } from '../../types/audit';

interface AuditDetailModalProps {
  record: AuditRecord | null;
  isOpen: boolean;
  onClose: () => void;
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({ record, isOpen, onClose }) => {
  if (!isOpen || !record) return null;

  const eventDisplay = getEventTypeDisplay(record.eventType);
  const actionColor = getActionColor(record.action);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Audit Record Details</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {formatTimestamp(record.timestamp)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
            {/* Event Badge */}
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${eventDisplay.color}`}>
                {eventDisplay.label}
              </span>
              <span className={`text-sm font-medium ${actionColor}`}>
                {record.action}
              </span>
            </div>

            {/* Description */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">{record.description}</p>
              </div>
            </div>

            {/* Item Information */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Package className="h-4 w-4 mr-2" />
                Item Information
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Name:</span>
                  <span className="text-sm font-medium text-gray-900">{record.targetName}</span>
                </div>
                {record.targetSerial && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Serial Number:</span>
                    <span className="text-sm font-medium text-gray-900">{record.targetSerial}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Target Type:</span>
                  <span className="text-sm text-gray-900">{record.targetType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Target ID:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.targetId}</span>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <User className="h-4 w-4 mr-2" />
                Performed By
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Email:</span>
                  <span className="text-sm font-medium text-gray-900">{record.actorEmail}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Role:</span>
                  <span className="text-sm text-gray-900">{record.actorRole}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">User ID:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.actorUserId}</span>
                </div>
              </div>
            </div>

            {/* Key Changes */}
            {record.keyChanges && Object.keys(record.keyChanges).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Key Changes
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {Object.entries(record.keyChanges).map(([field, change]) => {
                    if (!change) return null;
                    return (
                      <div key={field} className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 min-w-[120px]">
                          {field}:
                        </span>
                        <span className="text-sm text-red-600 line-through">
                          {change.from || '(empty)'}
                        </span>
                        <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-green-600 font-medium">
                          {change.to || '(empty)'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Changed Fields */}
            {record.changedFields && record.changedFields.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Fields Modified</h3>
                <div className="flex flex-wrap gap-2">
                  {record.changedFields.map((field) => (
                    <span
                      key={field}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Database className="h-4 w-4 mr-2" />
                Metadata
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Audit ID:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.auditId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Source Function:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.sourceFunction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Source Table:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.sourceTable}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Tenant ID:</span>
                  <span className="text-sm font-mono text-gray-600 text-xs">{record.tenantId}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailModal;
